import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Search, Plus, Edit3, Trash2, Sparkles, X, Loader2,
    BookOpen, FileText, Zap, ChevronRight, ChevronDown, Layers,
} from 'lucide-react';

const ACCENT = '#7C6BEF';
const AI_ORANGE = '#F59E0B';
const WEIGHT_COLOR = { 1: '#9CA3AF', 2: '#3B82F6', 3: '#EF4444' };
const WEIGHT_LABEL = { 1: 'Estándar', 2: 'NOM', 3: 'Crítico' };

const CATEGORIES = [
    'Hospedaje', 'Alimentos y bebidas', 'Comercio', 'Entretenimiento',
    'Educación', 'Salud y bienestar', 'Servicios profesionales',
    'Industria y operación', 'Logística y movilidad', 'Otros', 'General',
];

const CATEGORY_COLORS = {
    'Hospedaje': '#7C6BEF',
    'Alimentos y bebidas': '#F59E0B',
    'Comercio': '#10B981',
    'Entretenimiento': '#EC4899',
    'Educación': '#3B82F6',
    'Salud y bienestar': '#EF4444',
    'Servicios profesionales': '#06B6D4',
    'Industria y operación': '#6366F1',
    'Logística y movilidad': '#A855F7',
    'Otros': '#6B7280',
    'General': '#7C6BEF',
};

export default function AdminRubros() {
    const [rubros, setRubros] = useState([]);
    const [standards, setStandards] = useState([]);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [generating, setGenerating] = useState(null);
    const [detailOf, setDetailOf] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get('/rubros'),
            api.get('/standards'),
        ])
            .then(([r, s]) => { setRubros(r.data); setStandards(s.data || []); })
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar rubros')))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return rubros;
        return rubros.filter((r) =>
            [r.name, r.description, r.category].filter(Boolean).join(' ').toLowerCase().includes(query)
        );
    }, [q, rubros]);

    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach((r) => {
            const cat = r.category || 'Otros';
            (map[cat] = map[cat] || []).push(r);
        });
        return map;
    }, [filtered]);

    const createRubro = async (data) => {
        try {
            await api.post('/rubros', data);
            toast.success('Rubro creado');
            setCreating(false);
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al crear')); }
    };

    const updateRubro = async (id, patch) => {
        try {
            await api.patch(`/rubros/${id}`, patch);
            toast.success('Rubro actualizado');
            setEditing(null);
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al actualizar')); }
    };

    const deleteRubro = async (id) => {
        try {
            await api.delete(`/rubros/${id}`);
            toast.success('Rubro eliminado');
            setDeleting(null);
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al eliminar')); }
    };

    const generateAi = async (rubro) => {
        setGenerating(rubro.id);
        try {
            const r = await api.post(`/rubros/${rubro.id}/generate-checklist`);
            toast.success(`${r.data.items?.length || 0} preguntas generadas por IA`);
            setRubros((prev) => prev.map((x) => x.id === rubro.id ? r.data : x));
            setDetailOf(r.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al generar con IA'));
        } finally {
            setGenerating(null);
        }
    };

    return (
        <AdminLayout title="Rubros">
            <PageHeader
                title="Rubros e Industrias"
                subtitle={`◆ Catálogo maestro · ${rubros.length} rubros · Cada rubro tiene su checklist propio`}
                accent={ACCENT}
            >
                <button
                    onClick={() => setCreating(true)}
                    data-testid="new-rubro-button"
                    className="text-white px-5 py-3 font-bold inline-flex items-center gap-2"
                    style={{ background: ACCENT }}
                >
                    <Plus className="w-4 h-4" /> Nuevo rubro
                </button>
            </PageHeader>

            <div className="mb-6 flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-4 py-3 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                    <Search className="w-4 h-4 opacity-60" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="rubros-search"
                        placeholder="Buscar rubro (ej. hotelería, restaurantes...)"
                        className="flex-1 bg-transparent border-0 outline-none text-sm"
                    />
                </div>
                <div className="text-xs uppercase tracking-widest font-bold px-4 py-3 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                    {filtered.length} / {rubros.length}
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center opacity-60">Cargando rubros...</div>
            ) : (
                <div className="space-y-8" data-testid="rubros-grid">
                    {Object.entries(grouped).map(([cat, list]) => (
                        <section key={cat}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-2 h-8" style={{ background: CATEGORY_COLORS[cat] || ACCENT }} />
                                <h2 className="font-display font-black text-2xl" style={{ color: 'var(--text-primary)' }}>{cat}</h2>
                                <div className="text-xs uppercase tracking-widest font-bold opacity-60">{list.length} rubros</div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {list.map((r) => (
                                    <RubroCard
                                        key={r.id}
                                        rubro={r}
                                        standards={standards}
                                        generating={generating === r.id}
                                        onEdit={() => setEditing(r)}
                                        onDelete={() => setDeleting(r)}
                                        onGenerateAI={() => generateAi(r)}
                                        onOpen={() => setDetailOf(r)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                    {filtered.length === 0 && (
                        <div className="p-12 text-center opacity-60" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                            No se encontraron rubros
                        </div>
                    )}
                </div>
            )}

            {(creating || editing) && (
                <RubroFormModal
                    rubro={editing}
                    onSave={(data) => (editing ? updateRubro(editing.id, data) : createRubro(data))}
                    onClose={() => { setCreating(false); setEditing(null); }}
                />
            )}
            {deleting && (
                <ConfirmDelete
                    rubro={deleting}
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => deleteRubro(deleting.id)}
                />
            )}
            {detailOf && (
                <RubroDetail
                    rubro={rubros.find((x) => x.id === detailOf.id) || detailOf}
                    standards={standards}
                    onClose={() => setDetailOf(null)}
                    onGenerateAI={() => generateAi(detailOf)}
                    generating={generating === detailOf.id}
                    onReload={load}
                />
            )}
        </AdminLayout>
    );
}

// ─────────────────────────────────────────────────────────────
// Rubro Card (matches the design from the mockup)
// ─────────────────────────────────────────────────────────────
function RubroCard({ rubro, standards, generating, onEdit, onDelete, onGenerateAI, onOpen }) {
    const itemsCount = rubro.items?.length || 0;
    const areasCount = rubro.areas?.length || 0;
    const regsCount = rubro.standard_ids?.length || 0;
    const linkedCodes = (standards || [])
        .filter((s) => (rubro.standard_ids || []).includes(s.id))
        .map((s) => s.code);
    return (
        <div
            data-testid={`rubro-card-${rubro.id}`}
            className="p-5 border transition-all hover:shadow-lg flex flex-col"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
        >
            {/* Badges + Actions */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1"
                        style={{ background: 'rgba(124,107,239,0.15)', color: ACCENT }}>
                        RUBRO
                    </span>
                    {rubro.is_ai_generated && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1"
                            style={{ background: 'rgba(245,158,11,0.15)', color: AI_ORANGE }}>
                            <Sparkles className="w-3 h-3" /> IA
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={onEdit} data-testid={`edit-rubro-${rubro.id}`}
                        className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10">
                        <Edit3 className="w-4 h-4 opacity-70 hover:opacity-100" />
                    </button>
                    <button onClick={onDelete} data-testid={`delete-rubro-${rubro.id}`}
                        className="p-1.5 hover:bg-red-500/10 text-red-500">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Name + Description */}
            <button onClick={onOpen} className="text-left mb-4">
                <div className="font-display font-black text-xl mb-1">{rubro.name}</div>
                <div className="text-xs opacity-60 line-clamp-2 min-h-[2rem]">
                    {rubro.description || 'Sin descripción'}
                </div>
            </button>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <Stat value={itemsCount} label="ITEMS" />
                <Stat value={areasCount} label="ÁREAS" />
                <Stat value={regsCount} label="REGS" />
            </div>

            {/* Códigos de normas vinculadas */}
            {linkedCodes.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                    {linkedCodes.slice(0, 4).map((code) => (
                        <span key={code}
                            className="text-[10px] font-mono px-1.5 py-0.5 border"
                            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary, #6B7280)' }}>
                            {code}
                        </span>
                    ))}
                    {linkedCodes.length > 4 && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 opacity-60">
                            +{linkedCodes.length - 4}
                        </span>
                    )}
                </div>
            )}

            {/* Sections */}
            <div className="border-t pt-3 space-y-2 flex-1" style={{ borderColor: 'var(--border)' }}>
                <SectionRow icon={BookOpen} label="Regulaciones" trailing={<ChevronRight className="w-4 h-4 opacity-50" />} onClick={onOpen} />
                <SectionRow icon={FileText} label="Relevamiento" trailing={<span className="text-sm font-bold opacity-70">{itemsCount}</span>} onClick={onOpen} />
                <SectionRow icon={Zap} label="Evaluación" trailing={<span className="text-sm font-bold opacity-70">{itemsCount}</span>} onClick={onOpen} />
            </div>

            {/* Generate with AI */}
            <button
                onClick={onGenerateAI}
                disabled={generating}
                data-testid={`generate-ai-${rubro.id}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 text-white px-4 py-3 font-bold disabled:opacity-60"
                style={{ background: `linear-gradient(90deg, ${AI_ORANGE}, #EF4444)` }}
            >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? 'Generando...' : itemsCount ? 'Regenerar con IA' : 'Generar con IA'}
            </button>
        </div>
    );
}

function Stat({ value, label }) {
    return (
        <div className="text-center">
            <div className="text-2xl font-display font-black" style={{ color: ACCENT }}>{value}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mt-0.5">{label}</div>
        </div>
    );
}

function SectionRow({ icon: Icon, label, trailing, onClick }) {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-center justify-between py-1.5 hover:opacity-100 opacity-90 transition-opacity"
        >
            <div className="flex items-center gap-2 text-sm">
                <Icon className="w-4 h-4 opacity-70" />
                <span className="font-medium">{label}</span>
            </div>
            {trailing}
        </button>
    );
}

// ─────────────────────────────────────────────────────────────
// Create/Edit Modal
// ─────────────────────────────────────────────────────────────
function RubroFormModal({ rubro, onSave, onClose }) {
    const [form, setForm] = useState({
        name: rubro?.name || '',
        description: rubro?.description || '',
        category: rubro?.category || 'Otros',
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
        setBusy(true);
        try {
            await onSave({
                name: form.name.trim(),
                description: form.description.trim(),
                category: form.category,
                color: CATEGORY_COLORS[form.category] || ACCENT,
            });
        } finally { setBusy(false); }
    };
    return (
        <Modal onClose={onClose} title={rubro ? `Editar rubro · ${rubro.name}` : 'Nuevo rubro'}>
            <form onSubmit={submit}>
                <Label>Nombre *</Label>
                <input value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="rubro-name-input"
                    placeholder="ej. Restaurantes, Hospitales, Gasolineras..."
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent mb-4"
                    style={{ borderColor: 'var(--border)' }} />

                <Label>Categoría</Label>
                <select value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="rubro-category-input"
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent mb-4"
                    style={{ borderColor: 'var(--border)' }}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                <Label>Descripción</Label>
                <textarea rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="rubro-description-input"
                    placeholder="Descripción del rubro y su alcance..."
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent resize-none mb-6"
                    style={{ borderColor: 'var(--border)' }} />

                <div className="flex gap-2">
                    <button type="submit" disabled={busy}
                        data-testid="rubro-save"
                        className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: ACCENT }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {rubro ? 'Guardar cambios' : 'Crear rubro'}
                    </button>
                    <button type="button" onClick={onClose} disabled={busy}
                        data-testid="rubro-cancel"
                        className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Delete Confirmation
// ─────────────────────────────────────────────────────────────
function ConfirmDelete({ rubro, onCancel, onConfirm }) {
    return (
        <Modal onClose={onCancel} title="Eliminar rubro">
            <p className="text-sm mb-2">¿Seguro que quieres eliminar <strong>{rubro.name}</strong>?</p>
            <p className="text-xs opacity-70 mb-6">
                Esta acción borrará el rubro, sus áreas y su checklist. No se puede deshacer.
            </p>
            <div className="flex gap-2">
                <button onClick={onConfirm}
                    data-testid="rubro-delete-confirm"
                    className="flex-1 px-5 py-2.5 font-bold text-white inline-flex items-center justify-center gap-2"
                    style={{ background: '#EF4444' }}>
                    <Trash2 className="w-4 h-4" /> Sí, eliminar
                </button>
                <button onClick={onCancel}
                    data-testid="rubro-delete-cancel"
                    className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Detail Drawer (ver checklist del rubro + editar items + vincular normas)
// ─────────────────────────────────────────────────────────────
function RubroDetail({ rubro, standards, onClose, onGenerateAI, generating, onReload }) {
    const [showRegs, setShowRegs] = useState(false);
    const [linkingStandards, setLinkingStandards] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // {id?, ...} — null = closed, {} = new
    const [dragging, setDragging] = useState(null);
    const items = useMemo(() => rubro.items || [], [rubro.items]);
    const byArea = useMemo(() => {
        const map = {};
        items.forEach((it) => {
            const a = it.area || 'General';
            (map[a] = map[a] || []).push(it);
        });
        return map;
    }, [items]);

    const linkedStandards = useMemo(
        () => standards.filter((s) => (rubro.standard_ids || []).includes(s.id)),
        [standards, rubro.standard_ids]
    );

    const saveItem = async (data) => {
        try {
            if (data.id) {
                await api.patch(`/rubros/${rubro.id}/items/${data.id}`, data);
                toast.success('Ítem actualizado');
            } else {
                await api.post(`/rubros/${rubro.id}/items`, data);
                toast.success('Ítem agregado');
            }
            setEditingItem(null);
            onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al guardar ítem')); }
    };

    const deleteItem = async (iid) => {
        if (!window.confirm('¿Eliminar este ítem del checklist?')) return;
        try {
            await api.delete(`/rubros/${rubro.id}/items/${iid}`);
            toast.success('Ítem eliminado');
            onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al eliminar')); }
    };

    const saveLinkedStandards = async (ids) => {
        try {
            await api.patch(`/rubros/${rubro.id}`, { standard_ids: ids });
            toast.success('Normas vinculadas');
            setLinkingStandards(false);
            onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al vincular normas')); }
    };

    const handleDragStart = (e, id) => {
        setDragging(id);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (_id) => { /* preventDefault handled inline */ };
    const handleDrop = async (targetId) => {
        if (!dragging || dragging === targetId) { setDragging(null); return; }
        const ids = items.map((i) => i.id);
        const from = ids.indexOf(dragging);
        const to = ids.indexOf(targetId);
        if (from < 0 || to < 0) return;
        const draggedItem = items[from];
        const targetItem = items[to];
        const crossArea = (draggedItem.area || '') !== (targetItem.area || '');
        const next = [...ids];
        next.splice(from, 1);
        next.splice(to, 0, dragging);
        setDragging(null);
        try {
            await api.post(`/rubros/${rubro.id}/items/reorder`, { ordered_ids: next });
            if (crossArea) {
                toast.warning(`Orden actualizado. Nota: moviste el ítem entre áreas (${draggedItem.area || 'General'} → ${targetItem.area || 'General'}). Edita el ítem si querés cambiar su "área" oficialmente.`);
            } else {
                toast.success('Orden actualizado');
            }
            onReload();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al reordenar')); }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="rubro-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-3xl overflow-y-auto shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-6 border-b flex items-start justify-between gap-3" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1"
                                style={{ background: 'rgba(124,107,239,0.15)', color: ACCENT }}>RUBRO</span>
                            {rubro.is_ai_generated && (
                                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1"
                                    style={{ background: 'rgba(245,158,11,0.15)', color: AI_ORANGE }}>
                                    <Sparkles className="w-3 h-3" /> IA
                                </span>
                            )}
                        </div>
                        <div className="font-display font-black text-3xl">{rubro.name}</div>
                        {rubro.description && <div className="text-sm opacity-70 mt-1">{rubro.description}</div>}
                    </div>
                    <button onClick={onClose} data-testid="drawer-close" className="p-2 hover:bg-black/5 dark:hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-3">
                        <Stat value={items.length} label="ITEMS" />
                        <Stat value={Object.keys(byArea).length} label="ÁREAS" />
                        <Stat value={linkedStandards.length} label="REGS" />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={onGenerateAI} disabled={generating}
                            data-testid="drawer-generate-ai"
                            className="inline-flex items-center justify-center gap-2 text-white px-4 py-3 font-bold disabled:opacity-60"
                            style={{ background: `linear-gradient(90deg, ${AI_ORANGE}, #EF4444)` }}>
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {generating ? 'Generando...' : items.length ? 'Regenerar con IA' : 'Generar con IA'}
                        </button>
                        <button onClick={() => setEditingItem({})}
                            data-testid="drawer-add-item"
                            className="inline-flex items-center justify-center gap-2 px-4 py-3 font-bold text-white"
                            style={{ background: ACCENT }}>
                            <Plus className="w-4 h-4" /> Agregar ítem
                        </button>
                    </div>

                    {/* Regulaciones (vincular normas) */}
                    <div>
                        <div className="flex items-center justify-between">
                            <button onClick={() => setShowRegs(!showRegs)}
                                className="flex items-center gap-2 py-2 font-bold">
                                <BookOpen className="w-4 h-4" /> Regulaciones ({linkedStandards.length})
                                {showRegs ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <button onClick={() => setLinkingStandards(true)}
                                data-testid="drawer-link-standards"
                                className="text-xs font-bold hover:underline" style={{ color: ACCENT }}>
                                Vincular normas
                            </button>
                        </div>
                        {showRegs && (
                            <div className="pl-6 py-2 text-sm space-y-1">
                                {linkedStandards.length === 0 ? (
                                    <div className="opacity-70">Sin normas asociadas. Haz clic en &quot;Vincular normas&quot;.</div>
                                ) : linkedStandards.map((s) => (
                                    <div key={s.id} className="flex items-center gap-2">
                                        <FileText className="w-3.5 h-3.5 opacity-60" />
                                        <span className="font-mono text-xs">{s.code}</span>
                                        <span className="opacity-70">— {s.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Checklist */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Layers className="w-4 h-4" />
                            <div className="font-display font-black text-lg">Checklist ({items.length})</div>
                        </div>
                        {items.length === 0 ? (
                            <div className="p-8 text-center opacity-60 border-dashed border-2 text-sm" style={{ borderColor: 'var(--border)' }}>
                                Este rubro aún no tiene ítems. Usa &quot;Agregar ítem&quot; o &quot;Generar con IA&quot;.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {Object.entries(byArea).map(([area, list]) => (
                                    <div key={area}>
                                        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: ACCENT }}>
                                            ▸ {area} · {list.length} ítems
                                        </div>
                                        <div className="space-y-2">
                                            {list.map((it) => (
                                                <ItemRow key={it.id} item={it}
                                                    onEdit={() => setEditingItem(it)}
                                                    onDelete={() => deleteItem(it.id)}
                                                    onDragStart={handleDragStart}
                                                    onDragOver={handleDragOver}
                                                    onDrop={handleDrop}
                                                    dragging={dragging} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {editingItem !== null && (
                    <ItemFormModal
                        item={editingItem}
                        allItems={items}
                        onSave={saveItem}
                        onClose={() => setEditingItem(null)}
                    />
                )}
                {linkingStandards && (
                    <LinkStandardsModal
                        allStandards={standards}
                        selectedIds={rubro.standard_ids || []}
                        onSave={saveLinkedStandards}
                        onClose={() => setLinkingStandards(false)}
                    />
                )}
            </div>
        </div>
    );
}

function ItemRow({ item, onEdit, onDelete, onDragStart, onDragOver, onDrop, dragging }) {
    const subformLabel = { none: '', per_instance: 'Por instancia', general: 'General' }[item.subform_type] || '';
    const typeLabel = {
        yes_no: 'Sí/No/N.A.', numeric: 'Numérico', text: 'Texto',
        options: 'Opciones', photo: 'Foto', date: 'Fecha',
    }[item.answer_type] || '';
    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, item.id)}
            onDragOver={(e) => { e.preventDefault(); onDragOver(item.id); }}
            onDrop={(e) => { e.preventDefault(); onDrop(item.id); }}
            className={`border p-3 cursor-move transition-opacity ${dragging === item.id ? 'opacity-40' : ''}`}
            style={{ borderColor: 'var(--border)' }}
            data-testid={`item-row-${item.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="text-[10px] uppercase tracking-widest opacity-40 shrink-0 select-none" title="Arrastrar">⋮⋮</div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        {typeLabel && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>{typeLabel}</span>
                        )}
                        {subformLabel && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7' }}>{subformLabel}</span>
                        )}
                        {item.requires_photo && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(245,158,11,0.15)', color: AI_ORANGE }}>+FOTO IA</span>
                        )}
                        {item.visibility_condition?.when_item_id && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(107,114,128,0.2)', color: '#6B7280' }}>CONDICIONAL</span>
                        )}
                    </div>
                    <p className="text-sm">{item.question}</p>
                    {item.tooltip && <div className="text-[11px] opacity-60 mt-1">💡 {item.tooltip}</div>}
                    {item.standard_reference && (
                        <div className="text-[11px] opacity-60 font-mono mt-1">📜 {item.standard_reference}</div>
                    )}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                    style={{ background: `${WEIGHT_COLOR[item.weight]}22`, color: WEIGHT_COLOR[item.weight] }}>
                    {WEIGHT_LABEL[item.weight]}
                </span>
                <button onClick={onEdit} data-testid={`edit-item-${item.id}`}
                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 shrink-0">
                    <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={onDelete} data-testid={`delete-item-${item.id}`}
                    className="p-1.5 hover:bg-red-500/10 text-red-500 shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Item Form Modal (full editor matching mockup)
// ─────────────────────────────────────────────────────────────
function ItemFormModal({ item, allItems, onSave, onClose }) {
    const isEdit = !!item.id;
    const [form, setForm] = useState({
        id: item.id,
        question: item.question || '',
        area: item.area || '',
        weight: item.weight || 2,
        standard_reference: item.standard_reference || '',
        answer_type: item.answer_type || 'yes_no',
        subform_type: item.subform_type || 'none',
        options: item.options || [],
        tooltip: item.tooltip || '',
        example_answer: item.example_answer || '',
        phase: item.phase || 'evaluacion',
        requires_photo: item.requires_photo || false,
        visibility_condition: item.visibility_condition || null,
    });
    const [busy, setBusy] = useState(false);
    const [showCondition, setShowCondition] = useState(!!item.visibility_condition);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.question.trim()) { toast.error('La pregunta es obligatoria'); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            if (!showCondition || !payload.visibility_condition?.when_item_id) {
                payload.visibility_condition = null;
            }
            if (payload.answer_type !== 'options') payload.options = [];
            await onSave(payload);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b sticky top-0 z-10 flex items-center justify-between gap-3"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <div className="font-display font-black text-lg">{isEdit ? 'Editar ítem' : 'Nuevo ítem'}</div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <Label>Pregunta *</Label>
                        <textarea rows={2} value={form.question} required
                            onChange={(e) => setForm({ ...form, question: e.target.value })}
                            data-testid="item-question-input"
                            placeholder="¿La empresa cuenta con...?"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent resize-none"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Tipo de respuesta</Label>
                            <select value={form.answer_type}
                                onChange={(e) => setForm({ ...form, answer_type: e.target.value })}
                                data-testid="item-answer-type"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }}>
                                <option value="yes_no">Sí / No / N.A.</option>
                                <option value="numeric">Numérico</option>
                                <option value="text">Texto</option>
                                <option value="options">Opciones</option>
                                <option value="photo">Sólo foto</option>
                                <option value="date">Fecha</option>
                            </select>
                        </div>
                        <div>
                            <Label>Tipo subformulario</Label>
                            <select value={form.subform_type}
                                onChange={(e) => setForm({ ...form, subform_type: e.target.value })}
                                data-testid="item-subform-type"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }}>
                                <option value="none">Sin subformulario</option>
                                <option value="per_instance">Uno por cada instancia</option>
                                <option value="general">Un formulario general</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Área relacionada</Label>
                            <input value={form.area}
                                onChange={(e) => setForm({ ...form, area: e.target.value })}
                                placeholder="Ej: Cocina, Baño, Recepción"
                                data-testid="item-area-input"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div>
                            <Label>Fase</Label>
                            <select value={form.phase}
                                onChange={(e) => setForm({ ...form, phase: e.target.value })}
                                data-testid="item-phase"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }}>
                                <option value="relevamiento">Relevamiento</option>
                                <option value="evaluacion">Evaluación</option>
                            </select>
                        </div>
                    </div>

                    {form.answer_type === 'options' && (
                        <div>
                            <Label>Opciones (una por línea)</Label>
                            <textarea rows={3} value={(form.options || []).join('\n')}
                                onChange={(e) => setForm({ ...form, options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                                placeholder={"Opción 1\nOpción 2\nOpción 3"}
                                data-testid="item-options-input"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent resize-none font-mono"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                    )}

                    <div>
                        <Label>Tooltip / Ayuda</Label>
                        <textarea rows={2} value={form.tooltip}
                            onChange={(e) => setForm({ ...form, tooltip: e.target.value })}
                            placeholder="Ayuda contextual para el auditor..."
                            data-testid="item-tooltip-input"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent resize-none"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>

                    <div>
                        <Label>Ejemplo de respuesta</Label>
                        <input value={form.example_answer}
                            onChange={(e) => setForm({ ...form, example_answer: e.target.value })}
                            placeholder="Ej: Sí, 4 cocinas"
                            data-testid="item-example-input"
                            className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Peso</Label>
                            <div className="flex gap-1">
                                {[1, 2, 3].map((w) => (
                                    <button key={w} type="button" onClick={() => setForm({ ...form, weight: w })}
                                        data-testid={`item-weight-${w}`}
                                        className="flex-1 px-2 py-2 border text-xs font-bold"
                                        style={{
                                            borderColor: form.weight === w ? WEIGHT_COLOR[w] : 'var(--border)',
                                            background: form.weight === w ? `${WEIGHT_COLOR[w]}22` : 'transparent',
                                            color: form.weight === w ? WEIGHT_COLOR[w] : 'inherit',
                                        }}>
                                        {WEIGHT_LABEL[w]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Norma / Referencia</Label>
                            <input value={form.standard_reference}
                                onChange={(e) => setForm({ ...form, standard_reference: e.target.value })}
                                placeholder="Ej: NOM-002-STPS-2010"
                                data-testid="item-standard-input"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.requires_photo}
                            onChange={(e) => setForm({ ...form, requires_photo: e.target.checked })}
                            data-testid="item-requires-photo"
                            className="w-4 h-4" />
                        <span>Requerir foto (analizada por IA para verificar evidencia)</span>
                    </label>

                    {/* Visibilidad condicional */}
                    <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                        <button type="button" onClick={() => setShowCondition(!showCondition)}
                            className="flex items-center gap-2 text-sm font-bold mb-2">
                            {showCondition ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <Zap className="w-4 h-4" style={{ color: AI_ORANGE }} />
                            Visibilidad condicional
                        </button>
                        {showCondition && (
                            <div className="pl-6 space-y-2">
                                <div className="text-[11px] opacity-70">Mostrar este ítem solo cuando otro ítem tenga cierto valor.</div>
                                <div className="grid grid-cols-3 gap-2">
                                    <select value={form.visibility_condition?.when_item_id || ''}
                                        onChange={(e) => setForm({
                                            ...form,
                                            visibility_condition: { ...(form.visibility_condition || {}), when_item_id: e.target.value, operator: form.visibility_condition?.operator || 'equals', value: form.visibility_condition?.value || '' },
                                        })}
                                        data-testid="item-cond-item"
                                        className="px-3 py-2 border outline-none text-xs bg-transparent"
                                        style={{ borderColor: 'var(--border)' }}>
                                        <option value="">Ítem…</option>
                                        {(allItems || []).filter((i) => i.id !== form.id).map((i) => (
                                            <option key={i.id} value={i.id}>{i.question.slice(0, 40)}</option>
                                        ))}
                                    </select>
                                    <select value={form.visibility_condition?.operator || 'equals'}
                                        onChange={(e) => setForm({
                                            ...form,
                                            visibility_condition: { ...(form.visibility_condition || {}), operator: e.target.value },
                                        })}
                                        data-testid="item-cond-op"
                                        className="px-3 py-2 border outline-none text-xs bg-transparent"
                                        style={{ borderColor: 'var(--border)' }}>
                                        <option value="equals">=</option>
                                        <option value="not_equals">≠</option>
                                        <option value="greater_than">&gt;</option>
                                        <option value="less_than">&lt;</option>
                                        <option value="contains">contiene</option>
                                    </select>
                                    <input value={form.visibility_condition?.value || ''}
                                        onChange={(e) => setForm({
                                            ...form,
                                            visibility_condition: { ...(form.visibility_condition || {}), value: e.target.value },
                                        })}
                                        placeholder="Valor"
                                        data-testid="item-cond-value"
                                        className="px-3 py-2 border outline-none text-xs bg-transparent"
                                        style={{ borderColor: 'var(--border)' }} />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 pt-2">
                        <button type="submit" disabled={busy}
                            data-testid="item-save"
                            className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            style={{ background: ACCENT }}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            {isEdit ? 'Guardar cambios' : 'Crear ítem'}
                        </button>
                        <button type="button" onClick={onClose} disabled={busy}
                            data-testid="item-cancel"
                            className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                            style={{ borderColor: 'var(--border)' }}>
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Link Standards Modal (multi-select)
// ─────────────────────────────────────────────────────────────
function LinkStandardsModal({ allStandards, selectedIds, onSave, onClose }) {
    const [sel, setSel] = useState(new Set(selectedIds));
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState('');
    const filtered = allStandards.filter((s) =>
        !q.trim() || (s.code + ' ' + s.name).toLowerCase().includes(q.toLowerCase())
    );
    const toggle = (id) => {
        const next = new Set(sel);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSel(next);
    };
    const save = async () => {
        setBusy(true);
        try { await onSave(Array.from(sel)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <div className="font-display font-black text-lg">Vincular normas al rubro</div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar norma..."
                        data-testid="link-std-search"
                        className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                        style={{ borderColor: 'var(--border)' }} />
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {allStandards.length === 0 ? (
                        <div className="text-center opacity-60 py-6">
                            No hay normas registradas. Crea o importa normas primero en la sección &quot;Normativas&quot;.
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center opacity-60 py-6">Sin resultados para &quot;{q}&quot;.</div>
                    ) : filtered.map((s) => (
                        <label key={s.id}
                            data-testid={`link-std-${s.id}`}
                            className="flex items-start gap-3 py-2 border-b hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
                            style={{ borderColor: 'var(--border)' }}>
                            <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)}
                                className="w-4 h-4 mt-1" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm">{s.code}</div>
                                <div className="text-xs opacity-70">{s.name}</div>
                            </div>
                        </label>
                    ))}
                </div>
                <div className="p-4 border-t flex gap-2 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={save} disabled={busy}
                        data-testid="link-std-save"
                        className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: ACCENT }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                        Guardar ({sel.size})
                    </button>
                    <button onClick={onClose} disabled={busy}
                        className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}

function Label({ children }) {
    return <label className="text-[10px] uppercase tracking-wider font-bold opacity-70 block mb-1.5">{children}</label>;
}

function Modal({ children, onClose, title }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center justify-between gap-3 sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <div className="font-display font-black text-lg">{title}</div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
